import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import { SETTLEMENT_TOKEN_KEY } from "constants/authCred";
import { useGetProfileQuery } from "api/settings";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [userProfile, setUserProfile] = useState(null);
  const [isLoggedIn, setLoggedIn] = useState(
    Boolean(sessionStorage.getItem(SETTLEMENT_TOKEN_KEY)),
  );

  const { data: profileData, error } = useGetProfileQuery({
    enabled: isLoggedIn && !userProfile,
  });

  const inactivityTimer = useRef(null);
  const INACTIVITY_LIMIT = 30 * 60 * 1000;

  const logout = useCallback(() => {
    setUserProfile(null);
    setLoggedIn(false);
    clearTimeout(inactivityTimer.current);
    sessionStorage.removeItem(SETTLEMENT_TOKEN_KEY);
    navigate("/login", { state: { pathname: location.pathname } });
  }, [location.pathname, navigate]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    if (isLoggedIn) {
      inactivityTimer.current = setTimeout(() => {
        enqueueSnackbar("You have been logged out due to inactivity", {
          variant: "warning",
        });
        logout();
      }, INACTIVITY_LIMIT);
    }
  }, [INACTIVITY_LIMIT, isLoggedIn, enqueueSnackbar, logout]);

  const login = useCallback(
    (profile) => {
      setUserProfile(profile);
      setLoggedIn(true);
      resetInactivityTimer();
      if (location?.state?.pathname) {
        navigate(location.state.pathname);
      }
    },
    [location?.state?.pathname, navigate, resetInactivityTimer],
  );

  useEffect(() => {
    if (!isLoggedIn) return;

    const activityEvents = [
      "mousemove",
      "mousedown",
      "click",
      "scroll",
      "keypress",
    ];

    const handleActivity = () => resetInactivityTimer();

    activityEvents.forEach((event) =>
      window.addEventListener(event, handleActivity),
    );

    resetInactivityTimer();

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
      clearTimeout(inactivityTimer.current);
    };
  }, [isLoggedIn, resetInactivityTimer]);

  useEffect(() => {
    if (profileData) {
      setUserProfile(profileData);
    }
  }, [profileData]);

  useEffect(() => {
    if (error?.statusCode === 401) {
      enqueueSnackbar("Session Expired Please Login to Continue", {
        variant: "error",
      });
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const data = {
    isLoggedIn,
    userProfile,
    logout,
    login,
  };

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};
